import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close, ExpandMore, Refresh, MailOutline } from "@mui/icons-material";
import { filterLogRows, formatLogDate, normalizeLogRows } from "../../../model/reportLogs";

import { reportHeaderSx, reportPaperSx, reportFooterSx } from "./reportModalStyles";

const colors = { ink: "#243b53", muted: "#526579", border: "#dce5ed" };
const DeliveryChip = ({ delivery }) => (
  <Chip
    size="small"
    label={delivery.label}
    sx={{ color: delivery.color, bgcolor: delivery.background, fontWeight: 600 }}
  />
);
DeliveryChip.propTypes = { delivery: PropTypes.object.isRequired };

function LogDetails({ row, onClose }) {
  return (
    <Dialog
      open={Boolean(row)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="mail-log-detail-title"
      PaperProps={{ sx: reportPaperSx }}
    >
      <DialogTitle
        id="mail-log-detail-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          ...reportHeaderSx,
        }}
      >
        Detalle del registro #{row?.logId}
        <IconButton onClick={onClose} aria-label="Cerrar detalle del correo">
          <Close />
        </IconButton>
      </DialogTitle>
      {row && (
        <DialogContent dividers>
          <DeliveryChip delivery={row.delivery} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {[
              ["Destinatario", row.recipient],
              ["Comercio", row.commerce],
              ["Fecha del registro · Bogotá", formatLogDate(row.createdAt)],
              ["Reporte", row.report],
              ["Estado del registro", row.operationStatus],
              ["Respuesta HTTP", row.httpStatus ?? "Sin información"],
              ["Estado del proveedor", row.providerStatus?.name || "Sin confirmación"],
              ["Descripción del proveedor", row.providerStatus?.description || "Sin información"],
              ["ID del mensaje", row.messageId || "Sin información"],
              ["Origen", row.raw.source || "Sin información"],
              ["Respuesta del servicio", row.responseMessage || "Sin información"],
            ].map(([label, value]) => (
              <Grid item xs={12} sm={6} key={label}>
                <Typography variant="caption" sx={{ color: colors.muted }}>
                  {label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.ink, fontWeight: 500, overflowWrap: "anywhere" }}
                >
                  {String(value)}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <Accordion
            disableGutters
            elevation={0}
            sx={{ mt: 3, border: `1px solid ${colors.border}` }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="body2">Datos técnicos del registro</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: "#f5f7fa",
                  fontSize: "0.75rem",
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                  maxHeight: 300,
                  overflow: "auto",
                }}
              >
                {JSON.stringify(row.raw, null, 2)}
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
      )}
      <DialogActions sx={reportFooterSx}>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
LogDetails.propTypes = { row: PropTypes.object, onClose: PropTypes.func.isRequired };

export default function LogsModal({ open, onClose, commerce, logs, loading, error, onRefresh }) {
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    setSearch("");
    setStart("");
    setEnd("");
    setPage(0);
    setSelected(null);
  }, [open, commerce?.comercioId]);
  useEffect(() => {
    setPage(0);
  }, [search, start, end, logs]);
  const rows = useMemo(() => normalizeLogRows(logs), [logs]);
  const filtered = useMemo(
    () => filterLogRows(rows, { search, start, end }),
    [rows, search, start, end]
  );
  const stats = useMemo(
    () => ({
      records: new Set(filtered.map((row) => row.logId)).size,
      recipients: filtered.filter((row) => row.hasRecipient).length,
    }),
    [filtered]
  );
  const invalidRange = Boolean(start && end && start > end);
  const visible = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const refresh = () => onRefresh(commerce.comercio, commerce.comercioId, true);
  if (!commerce) return null;
  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="xl"
        aria-labelledby="mail-logs-title"
        PaperProps={{ sx: reportPaperSx }}
      >
        <DialogTitle
          id="mail-logs-title"
          sx={{
            ...reportHeaderSx,
            color: "#fff",
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            p: 2.5,
          }}
        >
          <MailOutline />
          <Box sx={{ flex: 1 }}>
            <Typography
              component="span"
              sx={{ fontSize: "1.05rem", fontWeight: 700, color: "#fff" }}
            >
              Registro de correos
            </Typography>
            <Typography variant="body2" sx={{ color: "#dbe8f2" }}>
              {commerce.comercio}
              {commerce.comercioId !== "__unassigned__" ? ` · Código ${commerce.comercioId}` : ""}
            </Typography>
          </Box>
          <Button
            startIcon={<Refresh />}
            onClick={refresh}
            disabled={loading}
            sx={{ color: "#fff" }}
          >
            Actualizar
          </Button>
          <IconButton
            onClick={onClose}
            aria-label="Cerrar registro de correos"
            sx={{ color: "#fff" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        {loading && <LinearProgress aria-label="Cargando registro de correos" />}
        <DialogContent
          sx={{
            p: { xs: 2, md: 3 },
            bgcolor: "#f7f9fc",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflowY: { xs: "auto", md: "hidden" },
          }}
        >
          {error ? (
            <Alert severity="error" action={<Button onClick={refresh}>Reintentar</Button>}>
              {error}
            </Alert>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  flexShrink: 0,
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 1.5,
                  my: 2,
                }}
              >
                {[
                  ["Registros", stats.records, "#eaf2ff", "#245a83"],
                  ["Destinatarios", stats.recipients, "#edf5f8", "#245a83"],
                ].map(([label, value, background, color]) => (
                  <Paper
                    key={label}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: background,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: colors.muted }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, color }}>
                      {loading ? "—" : value.toLocaleString("es-CO")}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: { xs: 300, md: 0 },
                }}
              >
                <Box sx={{ p: 2, display: "flex", gap: 1.5, flexWrap: "wrap", flexShrink: 0 }}>
                  <TextField
                    label="Buscar destinatario, reporte o ID"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: "1 1 250px" }}
                  />
                  <TextField
                    type="date"
                    label="Desde"
                    size="small"
                    value={start}
                    onChange={(e) => setStart(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={invalidRange}
                    inputProps={{ max: end || undefined }}
                  />
                  <TextField
                    type="date"
                    label="Hasta"
                    size="small"
                    value={end}
                    onChange={(e) => setEnd(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    error={invalidRange}
                    inputProps={{ min: start || undefined }}
                  />
                  <Button
                    onClick={() => {
                      setSearch("");
                      setStart("");
                      setEnd("");
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
                {invalidRange && (
                  <Alert severity="warning">
                    La fecha inicial debe ser anterior o igual a la final.
                  </Alert>
                )}
                <Typography
                  variant="caption"
                  sx={{ px: 2, pb: 1.5, display: "block", color: colors.muted, flexShrink: 0 }}
                >
                  Fecha del registro en Bogotá · {filtered.length.toLocaleString("es-CO")} de{" "}
                  {rows.length.toLocaleString("es-CO")} filas.
                </Typography>
                <TableContainer
                  sx={{ flex: 1, minHeight: 100, borderRadius: 0, boxShadow: "none" }}
                >
                  <Table
                    stickyHeader
                    size="small"
                    aria-label="Historial de correos por destinatario"
                    sx={{
                      minWidth: 850,
                      tableLayout: "fixed",
                      "& th": {
                        bgcolor: "#eef3f8",
                        color: colors.muted,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                      },
                      "& td": { fontSize: "0.8rem", color: colors.ink, py: 1.5 },
                    }}
                  >
                    <colgroup>
                      {[20, 28, 18, 24, 10].map((width, index) => (
                        <col key={index} style={{ width: `${width}%` }} />
                      ))}
                    </colgroup>
                    <TableHead sx={{ display: "table-header-group", p: 0 }}>
                      <TableRow>
                        {[
                          "Fecha · Bogotá",
                          "Destinatario",
                          "Reporte / registro",
                          "ID del mensaje",
                          "Detalle",
                        ].map((label) => (
                          <TableCell key={label}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!loading &&
                        visible.map((row) => (
                          <TableRow key={row.key} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {formatLogDate(row.createdAt)}
                            </TableCell>
                            <TableCell sx={{ maxWidth: 300, overflowWrap: "anywhere" }}>
                              {row.recipient}
                            </TableCell>
                            <TableCell>
                              {row.report}
                              <Typography
                                variant="caption"
                                display="block"
                                sx={{ color: colors.muted }}
                              >
                                #{row.logId}
                              </Typography>
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 230,
                                overflowWrap: "anywhere",
                                fontFamily: "monospace",
                              }}
                            >
                              {row.messageId || "—"}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => setSelected(row)}
                                aria-label={`Ver detalle del registro ${row.logId} para ${row.recipient}`}
                              >
                                Ver detalle
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      {!loading && !visible.length && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                            {rows.length
                              ? "No hay correos que coincidan con los filtros."
                              : "Este comercio no tiene registros de correos identificados."}
                          </TableCell>
                        </TableRow>
                      )}
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                            Cargando registro de correos…
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  sx={{ flexShrink: 0 }}
                  component="div"
                  count={filtered.length}
                  page={Math.min(page, Math.max(0, Math.ceil(filtered.length / pageSize) - 1))}
                  onPageChange={(_, value) => setPage(value)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Filas por página"
                  labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
              </Paper>
            </>
          )}
        </DialogContent>
        <DialogActions sx={reportFooterSx}>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <LogDetails row={selected} onClose={() => setSelected(null)} />
    </>
  );
}
LogsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  commerce: PropTypes.shape({
    comercio: PropTypes.string,
    comercioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  logs: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
};
